/**
 * useSubscriptionManagement Hook
 *
 * Manages user subscription functionality including:
 * - fetchUserCategoryLimit function
 * - Category limit enforcement
 * - Subscription state management
 * - Subscription caching
 * - Category display limits
 *
 * TODO: Create subscription state management
 * TODO: Integrate with category display limits
 */

import { useState, useCallback, useRef } from 'react'

const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL

export const useSubscriptionManagement = () => {
  const [subscriptionData, setSubscriptionData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const subscriptionCache = useRef({})
  const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  const fetchUserCategoryLimit = useCallback(async userId => {
    if (!userId) return 5

    const url = `${API_GATEWAY_URL}/k1/users/${userId}`
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          Accept: 'application/json'
        }
      })
      const data = await response.json()
      return data.subscription?.categoryLimit || 5
    } catch (error) {
      console.error('Error fetching user subscription:', error)
      return 5
    }
  }, [])

  const fetchUserSubscription = useCallback(
    async userId => {
      if (!userId) return null

      // Check cache first
      const cacheKey = `subscription_${userId}`
      const cachedData = subscriptionCache.current[cacheKey]
      const now = Date.now()

      if (cachedData && now - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data
      }

      const url = `${API_GATEWAY_URL}/k1/users/${userId}`
      setIsLoading(true)

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: 'application/json'
          }
        })
        const data = await response.json()

        const subscriptionInfo = {
          categoryCount: data.categoryCount,
          categoryLimit: data.subscription?.categoryLimit || 5,
          subscriptionType: data.subscription?.type,
          subscriptionStatus: data.subscription?.status,
          fullData: data
        }

        // Cache the result
        subscriptionCache.current[cacheKey] = {
          data: subscriptionInfo,
          timestamp: now
        }

        setSubscriptionData(subscriptionInfo)
        return subscriptionInfo
      } catch (error) {
        console.error('Error fetching user subscription:', error)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [CACHE_DURATION]
  )

  // Category limit enforcement logic
  const checkCategoryLimit = useCallback(
    async (userId, showUpgradeCallback) => {
      try {
        const subscriptionInfo = await fetchUserSubscription(userId)
        if (!subscriptionInfo) {
          if (showUpgradeCallback) {
            showUpgradeCallback('Failed to fetch subscription data', true)
          }
          return {
            allowed: false,
            message: 'Failed to fetch subscription data'
          }
        }

        const { categoryCount, categoryLimit } = subscriptionInfo

        if (categoryCount >= categoryLimit) {
          if (showUpgradeCallback) {
            showUpgradeCallback('You have reached your category limit.', true)
          }
          return {
            allowed: false,
            message: 'You have reached your category limit.',
            categoryCount,
            categoryLimit
          }
        }

        return {
          allowed: true,
          message: 'Category creation allowed',
          categoryCount,
          categoryLimit
        }
      } catch (error) {
        console.error('Error checking category limit:', error)
        const errorMessage = `Failed to fetch category count: ${error.message}`
        if (showUpgradeCallback) {
          showUpgradeCallback(errorMessage, true)
        }
        return { allowed: false, message: errorMessage }
      }
    },
    [fetchUserSubscription]
  )

  // Apply category limits based on subscription (from useNavigation.js)
  const applyCategoryLimits = useCallback((navigation, categoryLimit) => {
    return navigation?.map(superElm => {
      // Limiting the subCategories based on the subscription state for Kin-Categories
      if (superElm.name === 'Kin-Categories') {
        return {
          ...superElm,
          subCategories: superElm.subCategories.slice(0, categoryLimit)
        }
      }
      return superElm
    })
  }, [])

  // Clear subscription cache for a specific user or all users
  const clearSubscriptionCache = useCallback((userId = null) => {
    if (userId) {
      const cacheKey = `subscription_${userId}`
      delete subscriptionCache.current[cacheKey]
    } else {
      subscriptionCache.current = {}
    }
  }, [])

  // Get cached subscription data without making API call
  const getCachedSubscription = useCallback(
    userId => {
      if (!userId) return null

      const cacheKey = `subscription_${userId}`
      const cachedData = subscriptionCache.current[cacheKey]
      const now = Date.now()

      if (cachedData && now - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data
      }
      return null
    },
    [CACHE_DURATION]
  )

  // Update subscription data in cache (useful after successful category creation)
  const updateSubscriptionCache = useCallback((userId, updatedData) => {
    if (!userId) return

    const cacheKey = `subscription_${userId}`
    const now = Date.now()

    subscriptionCache.current[cacheKey] = {
      data: {
        ...updatedData,
        // Increment category count if a new category was created
        categoryCount: (updatedData.categoryCount || 0) + 1
      },
      timestamp: now
    }
  }, [])

  return {
    // Core subscription functions
    fetchUserCategoryLimit,
    fetchUserSubscription,
    checkCategoryLimit,
    applyCategoryLimits,

    // Cache management
    clearSubscriptionCache,
    getCachedSubscription,
    updateSubscriptionCache,

    // State
    subscriptionData,
    isLoading
  }
}

export default useSubscriptionManagement
